import {
    Check as CheckIcon,
    Close as CloseIcon,
    Description as DescriptionIcon,
    Gavel as GavelIcon,
    Layers as LayersIcon,
    Star as StarIcon
} from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent, CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import UserProfileService from "../UserProfileService";
import StripeProductService from "./StripeProductService";
import { filterMonthlyOnlyStripePrices, getStripePricePlanLabel } from "../../subscription/PanelPlan";
import { usePagesTranslation } from "../../locales";
import { SURFACE } from "../../../dark-theme/palette";
import { noticeSurfaceSx } from "../../../dark-theme/surfaces";
/**
 * ChangeSubscriptionDialog Component
 * 
 * Displays available subscription prices from Stripe for the puzzle subscription product
 * (Puzzle Premium) and allows users to change their subscription period/price
 * 
 * Policies:
 * - Immediate upgrades with proration (impulse-driven, users want access now)
 * - Deferred downgrades at period end (churn control, avoids refund friction)
 */
const ChangeSubscriptionDialog = ({ open, onClose, onSuccess, currentSubscription }) => {
    const { t } = usePagesTranslation();
    const [product, setProduct] = useState(null);
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [isUpgrade, setIsUpgrade] = useState(false);
    const [isCurrentPlan, setIsCurrentPlan] = useState(false);

    // Load product and prices
    const loadProductData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const puzzleSubscriptionProduct = await StripeProductService.getPuzzleSubscriptionProduct();
            
            if (!puzzleSubscriptionProduct) {
                setError('Puzzle Premium product not found');
                return;
            }
            
            setProduct(puzzleSubscriptionProduct);
            
            // Extract and format prices
            if (puzzleSubscriptionProduct.prices && Array.isArray(puzzleSubscriptionProduct.prices)) {
                const formattedPrices = puzzleSubscriptionProduct.prices.map(price => {
                    const payload = price.payload || {};
                    const unitAmount = payload.unit_amount || 0;
                    const currency = payload.currency || 'usd';
                    const recurring = payload.recurring || {};
                    const interval = recurring.interval || 'month';
                    const intervalCount = recurring.interval_count || 1;
                    const metadata = payload.metadata || {};
                    
                    // Format price
                    const formattedPrice = (unitAmount / 100).toFixed(2);
                    const priceDisplay = `$${formattedPrice}`;
                    
                    // Determine period display
                    let periodDisplay = '';
                    if (intervalCount === 1) {
                        periodDisplay = interval === 'month' ? 'month' : interval;
                    } else {
                        periodDisplay = `${intervalCount} ${interval}s`;
                    }
                    
                    // Extract planId from metadata or construct from price data
                    const planId = metadata.planId || metadata.plan_id ||
                        `STANDARD_${intervalCount}${interval.charAt(0).toUpperCase()}`;
                    
                    return {
                        id: price.id,
                        stripePriceId: price.stripePriceId,
                        priceDisplay,
                        unitAmount,
                        currency: currency.toUpperCase(),
                        interval,
                        intervalCount,
                        periodDisplay,
                        planId,
                        metadata,
                        payload
                    };
                });
                
                // Sort by interval count (monthly, quarterly, semi-annual, etc.)
                formattedPrices.sort((a, b) => {
                    if (a.interval !== b.interval) {
                        return a.interval === 'month' ? -1 : 1;
                    }
                    return a.intervalCount - b.intervalCount;
                });

                const visiblePrices = filterMonthlyOnlyStripePrices(formattedPrices);

                setPrices(visiblePrices);

                // Set first price as default selection
                if (visiblePrices.length > 0) {
                    const defaultPrice = visiblePrices[0];
                    setSelectedPrice(defaultPrice);
                    // Determine if it's an upgrade or downgrade
                    updateUpgradeStatus(defaultPrice, visiblePrices);
                }
            }
        } catch (error) {
            Logger.error('Error loading product data:', error);
            setError(error.message || 'Failed to load subscription options. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentSubscription]);

    // Determine if selected price is an upgrade or downgrade, and if it's the current plan
    const updateUpgradeStatus = useCallback((price, allPrices) => {
        if (!currentSubscription || !price || !allPrices || allPrices.length === 0) {
            setIsUpgrade(false);
            setIsCurrentPlan(false);
            return;
        }

        const cloudUser = currentSubscription.cloudUser || {};
        const currSub = cloudUser.currSub || {};
        
        // Try to find current price in the prices array by matching stripePriceId
        const currentPriceId = currSub.stripePriceId || currSub.priceId;
        const currentPrice = allPrices.find(p => p.stripePriceId === currentPriceId);
        
        // Check if selected price is the current plan
        if (currentPriceId && price.stripePriceId === currentPriceId) {
            setIsCurrentPlan(true);
            setIsUpgrade(false);
            return;
        } else {
            setIsCurrentPlan(false);
        }
        
        // If we found the current price, compare monthly equivalents
        if (currentPrice) {
            const currentMonthlyPrice = currentPrice.unitAmount / currentPrice.intervalCount;
            const selectedMonthlyPrice = price.unitAmount / price.intervalCount;
            setIsUpgrade(selectedMonthlyPrice > currentMonthlyPrice);
        } else {
            // Fallback: compare by last payment amount if available
            const lastPaymentAmount = currSub.lastPaymentAmount || 0;
            if (lastPaymentAmount > 0) {
                // Estimate current monthly price (assuming last payment was for the full period)
                // This is approximate - better to have stripePriceId in currSub
                const estimatedCurrentMonthly = lastPaymentAmount; // Simplified assumption
                const selectedMonthlyPrice = price.unitAmount / price.intervalCount;
                setIsUpgrade(selectedMonthlyPrice > estimatedCurrentMonthly);
            } else {
                // Default to false if we can't determine
                setIsUpgrade(false);
            }
        }
    }, [currentSubscription]);

    useEffect(() => {
        if (selectedPrice && prices.length > 0) {
            updateUpgradeStatus(selectedPrice, prices);
        }
    }, [selectedPrice, prices, updateUpgradeStatus]);

    useEffect(() => {
        if (open) {
            loadProductData();
        }
    }, [open, loadProductData]);

    const handleChangeSubscription = async () => {
        if (!selectedPrice) {
            setError('Please select a subscription plan');
            return;
        }

        try {
            setIsProcessing(true);
            setError('');

            // Calculate months from interval and intervalCount
            // For monthly plans: intervalCount = 1, interval = 'month' -> months = 1
            // For quarterly plans: intervalCount = 3, interval = 'month' -> months = 3
            // For semi-annual plans: intervalCount = 6, interval = 'month' -> months = 6
            let months = 1;
            if (selectedPrice.interval === 'month') {
                months = selectedPrice.intervalCount;
            } else if (selectedPrice.interval === 'year') {
                months = selectedPrice.intervalCount * 12;
            }

            // Update subscription directly using Stripe API
            const result = await UserProfileService.changePlan(
                selectedPrice.stripePriceId,
                months
            );

            if (result && result.code === "FAILURE") {
                throw new Error(result.message || 'Failed to update subscription');
            }

            // Success - close dialog and reload data
            if (onSuccess) {
                onSuccess();
            }
            handleClose();
        } catch (error) {
            Logger.error('Error changing subscription:', error);
            setError(error.message || 'Failed to change subscription. Please try again.');
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        setError('');
        setSelectedPrice(null);
        setIsProcessing(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: '#ffd700', fontSize: 28 }} />
                    <Typography variant="h5" component="div">
                        Change Subscription Plan
                    </Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <CircularProgress />
                    </Box>
                ) : error && !product ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'flex-start' }}>
                            <DescriptionIcon sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }} fontSize="small" />
                            <Typography variant="body1" color="text.secondary" sx={{ flex: 1 }}>
                                Select a new subscription plan to change your current subscription period and price.
                            </Typography>
                        </Box>

                        {prices.length > 0 ? (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <LayersIcon sx={{ color: 'primary.main' }} fontSize="small" />
                                    <Typography variant="h6" component="span">
                                        Available Plans
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, pt: 2, flexWrap: 'wrap' }}>
                                    {prices.map((price) => (
                                        <Card
                                            key={price.id}
                                            sx={{
                                                flex: prices.length <= 4 ? 1 : { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.333% - 11px)' },
                                                minWidth: { xs: '100%', sm: '200px' },
                                                cursor: 'pointer',
                                                border: selectedPrice?.id === price.id ? '2px solid #3BA666' : `1px solid ${SURFACE.border}`,
                                                position: 'relative',
                                                overflow: 'visible',
                                                '&:hover': {
                                                    borderColor: '#3BA666'
                                                }
                                            }}
                                            onClick={() => {
                                                setSelectedPrice(price);
                                                updateUpgradeStatus(price, prices);
                                            }}
                                        >
                                            <CardContent sx={{ textAlign: 'center', pt: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                                    {selectedPrice?.id === price.id && (
                                                        <CheckIcon sx={{ color: '#3BA666', mr: 0.5 }} />
                                                    )}
                                                    <Typography variant="h6" gutterBottom>
                                                        {getStripePricePlanLabel(t, price)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                                    {price.priceDisplay}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    per {price.periodDisplay}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {price.currency}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No subscription plans available at this time.
                            </Alert>
                        )}

                        <Box sx={{
                            p: 2,
                            borderRadius: 1,
                            ...noticeSurfaceSx("warning")
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                <GavelIcon sx={{ color: 'inherit', mt: 0.1, flexShrink: 0 }} fontSize="small" />
                                <Typography variant="caption" color="inherit" sx={{ fontWeight: 'bold', display: 'block' }}>
                                    Subscription Change Policy:
                                </Typography>
                            </Box>
                            {selectedPrice && isCurrentPlan && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                                    <CheckIcon sx={{ color: '#3BA666', fontSize: '1rem', mt: 0.1, flexShrink: 0 }} />
                                    <Typography variant="caption" color="inherit" sx={{ display: 'block', flex: 1 }}>
                                        You are already on this plan
                                    </Typography>
                                </Box>
                            )}
                            {selectedPrice && !isCurrentPlan && !isUpgrade && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                                    <CheckIcon sx={{ color: '#3BA666', fontSize: '1rem', mt: 0.1, flexShrink: 0 }} />
                                    <Typography variant="caption" color="inherit" sx={{ display: 'block', flex: 1 }}>
                                        <strong>Downgrade:</strong> Takes effect at the end of your current billing period
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                                <CheckIcon sx={{ color: '#3BA666', fontSize: '1rem', mt: 0.1, flexShrink: 0 }} />
                                <Typography variant="caption" color="inherit" sx={{ display: 'block', flex: 1 }}>
                                    You can cancel anytime from your profile page
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={handleClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleChangeSubscription}
                    variant="contained"
                    disabled={isProcessing || !selectedPrice || loading || isCurrentPlan}
                    sx={{
                        backgroundColor: '#3BA666',
                        '&:hover': { backgroundColor: '#2e7d32' }
                    }}
                >
                    {isProcessing ? 'Processing...' : 'Change Plan'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeSubscriptionDialog;

